import { z } from "zod";

import { machineTypeSchema } from "../machine-type";
import { machineWithCorrugateTracksSchema } from "../machine-with-corrugate-tracks";

const x4PhysicalMachineSettingsSchema = z
	.object({
		longHeadParameters: z
			.object({
				maximumPosition: z.number(),
			})
			.passthrough(),
		gantryParameters: z
			.object({
				maximumCorrugateWidth: z.number(),
			})
			.passthrough(),
	})
	.passthrough();

export const x4MachineSchema = machineWithCorrugateTracksSchema.extend({
	machineType: z.literal(machineTypeSchema.enum.X4),
	physicalMachineSettings: x4PhysicalMachineSettingsSchema,
	printerIp: z.string().nullable(),
	printerPort: z.number(),
	printerEnabled: z.boolean(),
	stopMachineWhenTrackEmpty: z.boolean(),
});

// TODO @eb remove this function when the X4 aligns with our decided upon schemas
export const prepareX4MachineData = (data: unknown): unknown => {
	if (typeof data !== "object" || data === null) {
		return data;
	}

	const input = data as Record<string, unknown>;
	const securePrintConfigurationInput = input.securePrintConfiguration as { delayLabelRetrieval?: boolean } | undefined;

	const iotDevice = {
		hubName: input.iotHubName ?? "",
		deviceId: input.iotEdgeDeviceId ?? "",
		moduleId: input.iotEdgeModuleId ?? "",
		moduleVersion: "",
		lastProofOfLife: input.proofOfLifeUpdated,
	};

	let tracks = input.tracks;
	if (Array.isArray(tracks)) {
		tracks = tracks.map((t) => {
			if (typeof t !== "object" || t === null) {
				return t;
			}

			const track = t as Record<string, unknown>;

			let assignedMaterial = track.loadedCorrugate;
			if (typeof assignedMaterial === "object" && assignedMaterial !== null) {
				const material = assignedMaterial as Record<string, unknown>;

				assignedMaterial = {
					id: material.id ?? "",
					alias: material.alias ?? "",
					width: material.width ?? 0,
					thickness: material.thickness ?? 0,
					quality: material.quality ?? 0,
				};
			}

			return {
				...track,
				number: track.trackNumber ?? 0,
				isEmpty: track.isTrackEmpty ?? false,
				assignedMaterial,
			};
		});
	}

	const machineStopReason = input.stopReason as Record<string, unknown>;
	let stopReason: {
		stopCode: number;
		message: string;
		arguments: number[];
		isError: boolean;
	} = { stopCode: 0, message: "", arguments: [], isError: false };
	if (typeof machineStopReason === "object" && machineStopReason !== null) {
		stopReason = {
			stopCode: (machineStopReason.stopCode as number) ?? 0,
			message: (machineStopReason.stopReason as string) ?? "",
			arguments: Array.isArray(machineStopReason.arguments) ? machineStopReason.arguments.map(Number) : [],
			isError: machineStopReason.isError === true,
		};
	}

	let activeWarnings = Array.isArray(input.warnings) ? input.warnings : [];
	activeWarnings = activeWarnings.map((w: Record<string, unknown>) => ({
		code: w.code as number,
		message: w.message,
		arguments: Array.isArray(w.arguments) ? w.arguments.map(Number) : [],
	}));

	let workstations = input.workstations;
	if (!Array.isArray(workstations) || workstations.length < 1) {
		const activeErrors = Array.isArray(input.errors) ? input.errors : [];

		workstations = [
			{
				number: 1,
				alarms: activeErrors.map((e: Record<string, unknown>) => ({
					code: e.code,
					message: e.message,
					arguments: Array.isArray(e.arguments) ? e.arguments.map(String) : [],
				})),
				warnings: activeWarnings.map((w: Record<string, unknown>) => ({
					...w,
					arguments: Array.isArray(w.arguments) ? w.arguments.map(String) : [],
				})),
				stopReason: {
					code: stopReason.stopCode,
					message: stopReason.message,
					arguments: stopReason.arguments.map(String),
					isError: stopReason.isError,
				},
			},
		];
	}

	return {
		...input,
		machineType: "X4",
		iotDevice,
		stopReason,
		activeWarnings,
		tracks,
		plcVersion: input.plcVersion ?? "",
		securePrintConfiguration: {
			delayLabelRetrieval: securePrintConfigurationInput?.delayLabelRetrieval ?? false,
		},
		workstations,
	};
};

export type X4Machine = z.infer<typeof x4MachineSchema>;
